import { ErrorLink } from '@apollo/client/link/error'

import {
  CombinedGraphQLErrors,
  ErrorLike,
  Observable,
  ServerError
} from '@apollo/client'

import { refresh } from '../hooks/useSuspendedSession'

function checkForAuthenticationError(error: ErrorLike) {
  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some(
      error =>
        error.message === 'unauthorized' ||
        error.message === 'forbidden' ||
        error.extensions?.code === 'UNAUTHENTICATED' ||
        error.extensions?.code === 'FORBIDDEN'
    )
  }

  if (ServerError.is(error)) {
    return error.statusCode === 401 || error.statusCode === 403
  }

  return false
}

/**
 * Apollo Error Link that handles authentication errors and retries.
 *
 * Authentication Flow:
 * 1. If a GraphQL operation returns an auth error (401 or specific error message)
 * 2. Attempt to refresh the authentication state silently
 * 3. If refresh succeeds (still signed in), retry the original operation once
 * 4. If retry also fails with auth error, show error alert to user
 * 5. If refresh fails, AuthProvider will handle the redirect to sign-in
 *
 * Note: Multiple simultaneous auth errors will share the same refresh promise,
 * preventing duplicate refresh attempts.
 */
export const apolloErrorLink = new ErrorLink(
  ({ error, operation, forward }) => {
    // Check if this operation has already been retried
    const hasBeenRetried = operation.getContext().hasBeenRetried
    const isAuthError = checkForAuthenticationError(error)

    if (isAuthError && !hasBeenRetried) {
      return new Observable(observer => {
        refresh()
          .then(session => {
            if (session) {
              // Still authenticated, retry the operation once
              operation.setContext({
                hasBeenRetried: true
              })

              forward(operation).subscribe(observer)
            } else {
              // Not authenticated anymore
              observer.error(error)
            }
          })
          .catch(error => {
            console.error('[Auth] Error refreshing authentication:', error)

            observer.error(error)
          })
      })
    }

    // If this is a retry and we still get auth error, log out and show error
    if (isAuthError && hasBeenRetried) {
      console.error(
        '[Auth] Operation failed after refresh:',
        operation.operationName
      )
    }
  }
)
